export const CONVERSATION_VERDICT = {
  PASS: "pass",
  UNSAFE: "unsafe",
  UNKNOWN: "unknown",
  WARNING: "warning",
} as const;

export type ConversationVerdict =
  (typeof CONVERSATION_VERDICT)[keyof typeof CONVERSATION_VERDICT];

export const CONVERSATION_TRACE_AVAILABILITY = {
  AVAILABLE: "available",
  INCOMPLETE: "incomplete",
  MISSING: "missing",
} as const;

export type ConversationTraceAvailability =
  (typeof CONVERSATION_TRACE_AVAILABILITY)[keyof typeof CONVERSATION_TRACE_AVAILABILITY];

export const CONVERSATION_DETAIL_TAB = {
  OVERVIEW: "overview",
  MESSAGES: "messages",
  TRACE: "trace",
} as const;

export type ConversationDetailTab =
  (typeof CONVERSATION_DETAIL_TAB)[keyof typeof CONVERSATION_DETAIL_TAB];

export interface ConversationFilterValues {
  application?: string;
  verdict?: ConversationVerdict;
  trace?: ConversationTraceAvailability;
}

export type ConversationMessageRole =
  "system" | "user" | "assistant" | "tool" | "retrieval" | "guardrail" | "human";

export interface ConversationMessage {
  id: string;
  role: ConversationMessageRole;
  timestamp: string;
  summary: string;
}

export interface ConversationEvaluation {
  id: string;
  group: "deterministic" | "model" | "external" | "missing";
  name: string;
  result: ConversationVerdict | "indeterminate";
  summary: string;
  source: string;
}

export interface ConversationSystemContext {
  projectId?: string;
  projectName?: string;
  modelCardId?: string;
  modelCardName?: string;
  reviewActionId?: string;
}

export interface ConversationTraceSpan {
  id: string;
  type: "agent" | "model" | "retrieval" | "tool" | "guardrail" | "evaluator";
  name: string;
  duration: string;
  result: ConversationVerdict | "indeterminate";
}

export interface ConversationRecord {
  id: string;
  timestamp: string;
  userRole: string;
  application: string;
  environment: string;
  agent: string;
  vendor: string;
  model: string;
  promptSummary: string;
  responsePreview: string;
  verdict: ConversationVerdict;
  intervention: "allowed" | "recorded" | "escalated";
  failedEvaluators: number;
  hasPhi: boolean;
  traceAvailability: ConversationTraceAvailability;
  duration: string;
  dataClassification: string;
  systemContext?: ConversationSystemContext;
  /** Persisted application identifier used to open the application workspace. */
  aiAppId?: string;
  findingId?: string;
  traceId?: string;
  /** The span that best explains the conversation-level result. */
  spanId?: string;
  evaluatorResultId?: string;
  spanCount?: number;
  messages: readonly ConversationMessage[];
  evaluations: readonly ConversationEvaluation[];
  traceSpans: readonly ConversationTraceSpan[];
}
